import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../../hooks/usePageTitle';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DescriptionIcon from '@mui/icons-material/Description';
import MenuItem from '@mui/material/MenuItem';
import Layout from '../../components/Layout';
import adminService from '../../services/adminService';
import authService from '../../services/authService';
import { useUnion } from '../../contexts/UnionContext';
import axios from 'axios';

function UnionsAdmin() {
  const { t } = useTranslation();
  usePageTitle(t('nav.unions'));
  const { refreshUnions, switchUnion, isUnionAdmin, activeUnion } = useUnion();
  // Allow creating a union when the user has no union yet (first-time setup)
  const canCreateUnion = isUnionAdmin || !activeUnion;
  const [unions, setUnions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedUnion, setSelectedUnion] = useState(null);
  const ESTONIAN_BANKS = [
    { value: 'SWEDBANK', label: 'Swedbank' },
    { value: 'SEB',      label: 'SEB' },
    { value: 'LHV',      label: 'LHV Pank' },
    { value: 'LUMINOR',  label: 'Luminor Bank' },
    { value: 'COOP',     label: 'Coop Pank' },
    { value: 'BIGBANK',  label: 'Bigbank' },
    { value: 'INBANK',   label: 'Inbank' },
    { value: 'CITADELE', label: 'Citadele' },
    { value: 'TFBANK',   label: 'TF Bank' },
    { value: 'OTHER',    label: 'Muu / Other' },
  ];

  const FORUM_MODES = [
    { value: 'DELETABLE', label: t('unions.forumFull') },
    { value: 'CHANGEABLE', label: t('unions.forumChangeable') },
    { value: 'WRITABLE', label: t('unions.forumWritable') },
  ];

  const [formData, setFormData] = useState({
    name: '',
    registration_code: '',
    address: '',
    bank_name: '',
    bank_account: '',
    bills_send_day: 1,
    bills_due_day: 15,
    late_fee_percentage: '0.05',
    forum_mode: 'DELETABLE',
  });
  const [importAddress, setImportAddress] = useState('');
  const [importing, setImporting] = useState(false);

  // Address live search state
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressSearching, setAddressSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const addressSearchTimeout = useRef(null);
  const addressBoxRef = useRef(null);

  useEffect(() => {
    loadUnions();
  }, []);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (addressBoxRef.current && !addressBoxRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUnions = async () => {
    try {
      setLoading(true);
      const data = await adminService.getUnions();
      setUnions(Array.isArray(data) ? data : data.results || []);
      setError('');
    } catch (err) {
      console.error('Failed to load unions:', err);
      setError(t('unions.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (union = null) => {
    if (union) {
      setSelectedUnion(union);
      setFormData({
        name: union.name,
        registration_code: union.registration_code,
        address: union.address,
        bank_name: union.bank_name || '',
        bank_account: union.bank_account || '',
        bills_send_day: union.bills_send_day ?? 1,
        bills_due_day: union.bills_due_day ?? 15,
        late_fee_percentage: union.late_fee_percentage,
        forum_mode: union.forum_mode || 'DELETABLE',
      });
    } else {
      setSelectedUnion(null);
      setFormData({
        name: '',
        registration_code: '',
        address: '',
        bank_name: '',
        bank_account: '',
        billing_day: 1,
        late_fee_percentage: '0.05',
      });
    }
    setAddressSuggestions([]);
    setShowSuggestions(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUnion(null);
    setAddressSuggestions([]);
    setShowSuggestions(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, address: value });

    // Debounced live search
    clearTimeout(addressSearchTimeout.current);
    if (value.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    addressSearchTimeout.current = setTimeout(async () => {
      setAddressSearching(true);
      try {
        const results = await adminService.searchAddresses(value);
        // Prefer building-type results — they carry tunnus/ehr_code and apartments
        const buildings = results.filter(r => r.liikNimi === 'EHITISHOONE');
        const suggestions = buildings.length > 0 ? buildings : results;
        setAddressSuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);
      } catch (err) {
        console.error('Address search failed:', err);
        setAddressSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setAddressSearching(false);
      }
    }, 300);
  };

  const handleSelectAddress = (suggestion) => {
    const fullAddress = suggestion.taisaadress || suggestion.ipikkaadress || '';
    setFormData({ ...formData, address: fullAddress });
    setAddressSuggestions([]);
    setShowSuggestions(false);
  };

  const handleSubmit = async () => {
    try {
      if (selectedUnion) {
        await adminService.updateUnion(selectedUnion.id, formData);
        handleCloseDialog();
        loadUnions();
      } else {
        const newUnion = await adminService.createUnion(formData);
        handleCloseDialog();
        // Refresh context so the new union appears in the switcher,
        // then auto-switch to it so the user can immediately manage it.
        await refreshUnions();
        await switchUnion(newUnion.id);
        loadUnions();
      }
    } catch (err) {
      console.error('Failed to save union:', err);
      setError(t('unions.failedToSave'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('unions.deleteConfirm'))) return;

    try {
      await adminService.deleteUnion(id);
      loadUnions();
    } catch (err) {
      console.error('Failed to delete union:', err);
      setError(t('unions.failedToDelete'));
    }
  };

  const handleOpenImport = (union) => {
    setSelectedUnion(union);
    setImportAddress(union.address);
    setImportDialogOpen(true);
  };

  const handleImport = async () => {
    try {
      setImporting(true);
      const result = await adminService.populateUnionFromAddress(
        selectedUnion.id,
        importAddress
      );

      if (result.apartments && result.apartments.length > 0) {
        await adminService.importApartments(selectedUnion.id, result.apartments);
        alert(t('unions.importedApts', { count: result.apartments.length }));
      } else {
        alert(t('unions.noAptsInRegistry'));
      }

      setImportDialogOpen(false);
      loadUnions();
    } catch (err) {
      console.error('Failed to import:', err);
      setError(t('unions.failedImport', { error: err.response?.data?.error || err.message }));
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadAnnualReport = async (unionId, year) => {
    try {
      const res = await axios.get(`/api/unions/${unionId}/annual-report/${year}/`, {
        headers: authService.authHeader(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `annual_report_${unionId}_${year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(t('unions.failedReport'));
    }
  };

  return (
    <Layout>
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">{t('unions.title')}</Typography>
          {canCreateUnion && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              {t('unions.addUnion')}
            </Button>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.name')}</TableCell>
                  <TableCell>{t('unions.registrationCode')}</TableCell>
                  <TableCell>{t('onboarding.address')}</TableCell>
                  <TableCell>{t('unions.ehrCode')}</TableCell>
                  {isUnionAdmin && <TableCell align="right">{t('common.actions')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {unions.map((union) => (
                  <TableRow key={union.id} hover>
                    <TableCell>{union.name}</TableCell>
                    <TableCell>{union.registration_code}</TableCell>
                    <TableCell>{union.address}</TableCell>
                    <TableCell>{union.ehr_code || '-'}</TableCell>
                    {isUnionAdmin && (
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleDownloadAnnualReport(union.id, new Date().getFullYear())} title={t('unions.downloadReport', { year: new Date().getFullYear() })}>
                          <DescriptionIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleOpenImport(union)} title={t('unions.importFromRegistry')}>
                          <CloudDownloadIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleOpenDialog(union)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(union.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{selectedUnion ? t('unions.editUnion') : t('unions.addUnion')}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField fullWidth label={t('common.name')} name="name" value={formData.name} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label={t('unions.registrationCode')} name="registration_code" value={formData.registration_code} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12}>
                <Box ref={addressBoxRef} sx={{ position: 'relative' }}>
                  <TextField
                    fullWidth
                    label={t('onboarding.address')}
                    name="address"
                    value={formData.address}
                    onChange={handleAddressChange}
                    onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                    required
                    autoComplete="off"
                    InputProps={{
                      endAdornment: addressSearching ? (
                        <CircularProgress size={18} sx={{ mr: 1 }} />
                      ) : null,
                    }}
                  />
                  {showSuggestions && (
                    <Paper
                      elevation={4}
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1300,
                        maxHeight: 240,
                        overflowY: 'auto',
                        mt: 0.5,
                      }}
                    >
                      <List dense disablePadding>
                        {addressSuggestions.map((s, i) => (
                          <ListItem key={i} disablePadding>
                            <ListItemButton onMouseDown={() => handleSelectAddress(s)}>
                              <ListItemText
                                primary={s.taisaadress || s.ipikkaadress}
                                secondary={s.liikNimi || null}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  select
                  fullWidth
                  label={t('unions.bank')}
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleChange}
                >
                  <MenuItem value=""><em>{t('unions.selectBank')}</em></MenuItem>
                  {ESTONIAN_BANKS.map((b) => (
                    <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={7}>
                <TextField fullWidth label={t('unions.bankAccount')} name="bank_account" value={formData.bank_account} onChange={handleChange} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label={t('unions.sendBillsDay')} name="bills_send_day" type="number" inputProps={{ min: 1, max: 28 }} value={formData.bills_send_day} onChange={handleChange} helperText={t('unions.sendBillsDayHelper')} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label={t('unions.billsDueDay')} name="bills_due_day" type="number" inputProps={{ min: 1, max: 28 }} value={formData.bills_due_day} onChange={handleChange} helperText={t('unions.billsDueDayHelper')} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label={t('unions.lateFee')} name="late_fee_percentage" type="number" inputProps={{ step: 0.01 }} value={formData.late_fee_percentage} onChange={handleChange} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select fullWidth label={t('unions.forumMode')} name="forum_mode"
                  value={formData.forum_mode} onChange={handleChange}
                  helperText={t('unions.forumModeHelper')}
                >
                  {FORUM_MODES.map((m) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
            <Button onClick={handleSubmit} variant="contained">{t('common.save')}</Button>
          </DialogActions>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('unions.importTitle')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('unions.importDesc')}
            </Typography>
            <TextField
              fullWidth
              label={t('unions.addressQuery')}
              value={importAddress}
              onChange={(e) => setImportAddress(e.target.value)}
              helperText="e.g., 'Narva mnt 7 Tallinn'"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImportDialogOpen(false)} disabled={importing}>{t('common.cancel')}</Button>
            <Button onClick={handleImport} variant="contained" disabled={importing}>
              {importing ? t('common.importing') : t('common.import')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}

export default UnionsAdmin;
